import { createContext, useContext, useRef, useState, useCallback } from "react";
import io from "socket.io-client";
import { toast } from "react-toastify";
import { useAuth } from "../../../hooks/useAuth";

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const { user } = useAuth();

  const getSocketConfig = useCallback(() => {
    const isHttps = window.location.protocol === "https:";
    const port = isHttps ? 8443 : 8080;
    const domain = process.env.REACT_APP_ENV === "production" ? "accountv2.growthzilla.com" : "accountstagingv2.growthzilla.com";
    const env = process.env.REACT_APP_ENV === "production" ? "production" : process.env.REACT_APP_ENV === "staging" ? "staging" : "local";

    return {
      url: env === "local" ? "http://client.growthzilla.com:8081/dashboard" : `${window.location.protocol}//${domain}:${port}/dashboard`,
      env,
    };
  }, []);

  const connect = useCallback(() => {
    if (socketRef.current) return;
    const { url, env } = getSocketConfig();
    const socket = io(url, {
      transports: ["websocket"],
      query: { roomId: `${user?.id}-${env}` },
      reconnection: true,
      reconnectionAttempts: 5,
    });

    socket.on("connect", () => {
        /* eslint-disable no-console */
      console.log("Socket connected:", socket.id);
      setIsConnected(true);
    });

    socket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);
      setIsConnected(false);
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });

    socket.on("reportRun", (data) => {
      console.log("Received reportRun:", data);
      if (data) toast.success(data);
    });

    socketRef.current = socket;
  }, [user?.id, getSocketConfig]);

  const subscribe = useCallback((event, callback) => {
    if (!socketRef.current) return () => {};
    socketRef.current.on(event, callback);
    return () => socketRef.current?.off(event, callback);
  }, []);

  return <SocketContext.Provider value={{ isConnected, connect, subscribe }}>{children}</SocketContext.Provider>;
};

export const useSocket = () => useContext(SocketContext);
